import React, { memo, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Spacer } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import { listSenders } from 'services/SenderService';
import { getMarketingSendersRawList } from '../../utils/marketingSenderOptions';
import Step from './components/Step/index';
import BrowserMockCard from './components/BrowserMockCard';
import { formatMarketingSenderPostalAddress, getEmailFooterHtml } from './templates';

import './styles.scss';

const UnsubscribeSettings = memo(() => {
    const [emailFooterSrc, setEmailFooterSrc] = useState(null);
    const langISOInterface = useSelector((state) => state.config?.userData?.langISOInterface);

    const unsubscribedConfirmationSrc = useMemo(() => {
        const lang =
            langISOInterface != null && String(langISOInterface).trim() !== ''
                ? String(langISOInterface).toLowerCase().slice(0, 2)
                : 'en';
        const params = new URLSearchParams({
            lang,
            originEmail: 'no-reply@example.com',
            originName: 'Example Company',
        });
        return `${window.location.origin}/#/email-unsubscribed?${params.toString()}`;
    }, [langISOInterface]);

    useEffect(() => {
        let cancelled = false;
        const footerText = getLiteral('label_marketing_campaigns_unsubscribe_footer_text');
        const unsubscribeText = getLiteral('label_marketing_campaigns_unsubscribe_here');

        const applyPreview = (name, companyAddress) => {
            const html = getEmailFooterHtml(name, companyAddress, footerText, unsubscribeText);
            setEmailFooterSrc(`data:text/html;charset=utf-8,${encodeURIComponent(html)}`);
        };

        listSenders()
            .then((data) => {
                if (cancelled) return;
                const raw = getMarketingSendersRawList(data);
                const defaultSender = raw.find((sender) => sender.default) ?? raw[0];
                const name = defaultSender?.name ? String(defaultSender.name).trim() : '';
                const companyAddress = formatMarketingSenderPostalAddress(defaultSender);
                applyPreview(name, companyAddress);
            })
            .catch(() => {
                if (cancelled) return;
                applyPreview('', '');
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className="unsubscribe-settings">
            <div className="unsubscribe-settings__scroll-container">
                <div className="unsubscribe-settings__header">
                    <Text type="h6" bold>
                        {getLiteral('title_marketing_campaigns_unsubscribe_preview')}
                    </Text>
                    <Spacer height="8" />
                    <Text type="body" className="unsubscribe-settings__description">
                        {getLiteral('label_marketing_campaigns_unsubscribe_preview_desc')}
                    </Text>
                </div>

                <Spacer height="32" />

                <div className="unsubscribe-settings__steps">
                    <Step
                        number={1}
                        title={getLiteral('label_marketing_campaigns_unsubscribe_step_1')}
                    >
                        <div className="unsubscribe-settings__iframe-container">
                            <iframe
                                src={emailFooterSrc ?? 'about:blank'}
                                className="unsubscribe-settings__iframe"
                                title="Email footer preview"
                            />
                        </div>
                    </Step>

                    <Step
                        number={2}
                        title={getLiteral('label_marketing_campaigns_unsubscribe_step_2')}
                    >
                        <BrowserMockCard>
                            <iframe
                                src={unsubscribedConfirmationSrc}
                                className="unsubscribe-settings__iframe unsubscribe-settings__iframe--confirmation"
                                title="Unsubscribe confirmation preview"
                            />
                        </BrowserMockCard>
                    </Step>
                </div>
            </div>
        </div>
    );
});

UnsubscribeSettings.displayName = 'UnsubscribeSettings';

export default UnsubscribeSettings;
